import FinalMemorandum from "./FinalMemorandum";
import CompletionCommunication from "./CompletionCommunication";
import FinalEvidenceSufficiency from "./FinalEvidenceSufficiency";
import FinalMisstatementAssessment from "./FinalMisstatementAssessment";
import ExpertAssessment from "./ExpertAssessment";
import RelatedPartyAssessment from "./RelatedPartyAssessment";
import AccountingEstimates from "./AccountingEstimates";
import FinalAnalytics from "./FinalAnalytics";
import { useReportingResource } from "@/hooks/useReportingResource";
import WrittenRepresentations from "./WrittenRepresentations";
import LitigationAssessment from "./LitigationAssessment";
import Consult from "./Consultations";
import GC from "./GoingConcern";
import Subs from "./SubsequentEvents";
import { exactMoney } from "@/lib/exactMoney";
import { useEffect, useRef, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Check, X } from "lucide-react";
import EvidencePanel from "@/pages/modules/EvidencePanel";
import { AlertBanner, SectionCard, MetricCard } from "@/components/ui/design";

export default function Completion({ id }) {
  return (
    <Tabs defaultValue="aje">
      <TabsList className="subtabs mb-4 flex-wrap">
        <TabsTrigger value="aje" data-testid="cp-tab-aje">Salah Saji / AJE</TabsTrigger>
        <TabsTrigger value="communication" data-testid="cp-tab-communication">Komunikasi TCWG</TabsTrigger>
        <TabsTrigger value="memorandum" data-testid="cp-tab-memorandum">Memorandum Final</TabsTrigger>
        <TabsTrigger value="notes" data-testid="cp-tab-notes">Review Notes</TabsTrigger>
        <TabsTrigger value="estimates" data-testid="cp-tab-estimates">Estimasi Akuntansi</TabsTrigger>
        <TabsTrigger value="sufficiency" data-testid="cp-tab-sufficiency">Kecukupan Bukti Final</TabsTrigger>
        <TabsTrigger value="final-misstatement" data-testid="cp-tab-final-misstatement">Evaluasi Salah Saji Final</TabsTrigger>
        <TabsTrigger value="experts" data-testid="cp-tab-experts">Pakar / Auditor Lain</TabsTrigger>
        <TabsTrigger value="related" data-testid="cp-tab-related">Pihak Berelasi</TabsTrigger>
        <TabsTrigger value="legal" data-testid="cp-tab-legal">Litigasi & Kontinjensi</TabsTrigger>
        <TabsTrigger value="representation" data-testid="cp-tab-representation">Representasi Tertulis</TabsTrigger>
        <TabsTrigger value="analytics" data-testid="cp-tab-analytics">Analitis Final</TabsTrigger>
        <TabsTrigger value="gc" data-testid="cp-tab-gc">Going Concern</TabsTrigger>
        <TabsTrigger value="subs" data-testid="cp-tab-subs">Subsequent Events</TabsTrigger>
        <TabsTrigger value="consult" data-testid="cp-tab-consult">Konsultasi</TabsTrigger>
        <TabsTrigger value="complete" data-testid="cp-tab-complete">Completion</TabsTrigger>
      </TabsList>
      <TabsContent value="aje"><AJE key={id} id={id} /></TabsContent>
      <TabsContent value="communication"><CompletionCommunication id={id} /></TabsContent>
      <TabsContent value="memorandum"><FinalMemorandum id={id} /></TabsContent>
      <TabsContent value="notes"><Notes key={id} id={id} /></TabsContent>
      <TabsContent value="estimates"><AccountingEstimates id={id} /></TabsContent>
      <TabsContent value="sufficiency"><FinalEvidenceSufficiency id={id} /></TabsContent>
      <TabsContent value="final-misstatement"><FinalMisstatementAssessment id={id} /></TabsContent>
      <TabsContent value="experts"><ExpertAssessment id={id} /></TabsContent>
      <TabsContent value="related"><RelatedPartyAssessment id={id} /></TabsContent>
      <TabsContent value="legal"><LitigationAssessment id={id} /></TabsContent>
      <TabsContent value="representation"><WrittenRepresentations id={id} /></TabsContent>
      <TabsContent value="analytics"><FinalAnalytics id={id} /></TabsContent>
      <TabsContent value="gc"><GC id={id} /></TabsContent>
      <TabsContent value="subs"><Subs id={id} /></TabsContent>
      <TabsContent value="consult"><Consult id={id} /></TabsContent>
      <TabsContent value="complete"><Complete id={id} /></TabsContent>
    </Tabs>
  );
}

export function AJE({ id }) {
  const [rows, setRows] = useState([]); const [dash, setDash] = useState(null);
  const [f, setF] = useState({ description: "", account: "", debit_account: "", credit_account: "", amount: "0", mis_type: "Factual", corrected: false });
  const scope = useRef({ id, generation: 0, request: 0 });
  if (scope.current.id !== id) scope.current = { id, generation: scope.current.generation + 1, request: 0 };
  const [busy, setBusy] = useState(false);
  const current = generation => scope.current.id === id && scope.current.generation === generation;
  const load = async () => {
    const generation = scope.current.generation, request = ++scope.current.request;
    try {
      const [register, dashboard] = await Promise.all([api.get(`/engagements/${id}/misstatements`), api.get(`/engagements/${id}/misstatements/dashboard`)]);
      if (current(generation) && scope.current.request === request) { setRows(Array.isArray(register.data) ? register.data : []); setDash(dashboard.data); }
    } catch (error) {
      if (current(generation) && scope.current.request === request) { setRows([]); setDash(null); toast.error(getErrorMessage(error)); }
    }
  };
  useEffect(() => {
    setRows([]); setDash(null); setBusy(false); setF({ description: "", account: "", debit_account: "", credit_account: "", amount: "0", mis_type: "Factual", corrected: false }); load();
    return () => { scope.current.generation += 1; };
  }, [id]);
  const mutate = async (method, path, body, created = false) => {
    const generation = scope.current.generation;
    setBusy(true);
    try {
      await api[method](path, body);
      if (!current(generation)) return;
      if (created) { toast.success("AJE dibuat"); setF({ description: "", account: "", debit_account: "", credit_account: "", amount: "0", mis_type: "Factual", corrected: false }); }
      await load();
    } catch (error) {
      if (current(generation)) { if ([403,404].includes(error.response?.status)) { setRows([]); setDash(null); } toast.error(getErrorMessage(error)); }
    } finally { if (current(generation)) setBusy(false); }
  };
  const add = async () => {
    if (!f.description.trim()) return toast.error("Deskripsi wajib");
    // B-458: a correction posts to the Final TB as debit and credit; one side alone unbalances it.
    if (!!f.debit_account.trim() !== !!f.credit_account.trim()) return toast.error("Isi akun debit dan akun kredit sekaligus.");
    await mutate('post', `/engagements/${id}/misstatements`, { ...f, account: f.account || f.debit_account }, true);
  };
  const upd = async (m, body) => mutate('put', `/engagements/${id}/misstatements/${m.id}`, body);
  return (
    <div className="space-y-3">
      {dash?.results_restricted && <AlertBanner tone="warning">Ringkasan memerlukan akses ke seluruh sumber. Nilai yang tidak tersedia bukan berarti tidak ada salah saji.</AlertBanner>}
      {dash && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
          {[["Terdeteksi", exactMoney(dash.total_detected)], ["Dikoreksi", exactMoney(dash.corrected)], ["Tidak Dikoreksi", exactMoney(dash.uncorrected)], ["Proyeksi", exactMoney(dash.projected)], ["% OM", dash.pct_om == null ? "Belum tersedia" : dash.pct_om + "%", dash.exceeds_om], ["Belum Dievaluasi", dash.unevaluated_count ?? "Belum tersedia", dash.unevaluated_count > 0]].map(([l, v, w]) => (
            <div key={l} className={`bg-white border rounded-sm p-2 ${w ? "border-red-300" : "border-border"}`} data-testid={`aje-stat-${l}`}><div className={`text-base font-head font-bold tabular break-all ${w ? "text-red-600" : ""}`}>{v}</div><div className="text-[10px] uppercase text-muted-foreground">{l}</div></div>
          ))}
        </div>
      )}
      <div className="bg-white border border-border rounded-sm overflow-x-auto"><table className="w-full audit-table">
        <thead><tr><th>Ref</th><th>Deskripsi</th><th>Akun (debit / kredit)</th><th className="text-right">Jumlah</th><th>Tipe</th><th>Dikoreksi</th><th>Kesimpulan Auditor</th></tr></thead>
        <tbody>{rows.map((m) => (
          <tr key={m.id} className={!m.auditor_conclusion ? "bg-amber-50" : ""} data-testid={`aje-row-${m.id}`}>
            <td className="font-mono text-xs">{m.ref}</td><td>{m.description}</td><td className="text-xs" data-testid={`aje-accounts-${m.id}`}>{m.debit_account ? `D ${m.debit_account} / K ${m.credit_account}` : <span className="text-amber-800">{m.account} (satu sisi)</span>}</td>
            <td className="tabular text-right">{exactMoney(m.amount)}</td><td>{m.mis_type}</td>
            <td><Checkbox disabled={busy} checked={m.corrected} onCheckedChange={(v) => upd(m, { corrected: !!v })} data-testid={`aje-corr-${m.id}`} /></td>
            <td><Input disabled={busy} className="h-7 text-xs w-48" placeholder="wajib untuk evaluasi" defaultValue={m.auditor_conclusion} onBlur={(e) => e.target.value !== m.auditor_conclusion && upd(m, { auditor_conclusion: e.target.value })} data-testid={`aje-concl-${m.id}`} /></td>
          </tr>))}
          {rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-4">{dash?.results_restricted ? "Tidak ada salah saji yang dapat ditampilkan dengan akses saat ini." : "Belum ada salah saji."}</td></tr>}
        </tbody></table></div>
      <div className="flex flex-wrap gap-2 items-end bg-white border border-border rounded-sm p-3">
        <Input className="h-8 w-56" placeholder="Deskripsi (dari eksepsi audit)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="aje-new-desc" />
        <Input className="h-8 w-28" placeholder="Akun debit" value={f.debit_account} onChange={(e) => setF({ ...f, debit_account: e.target.value })} data-testid="aje-new-debit" />
        <Input className="h-8 w-28" placeholder="Akun kredit" value={f.credit_account} onChange={(e) => setF({ ...f, credit_account: e.target.value })} data-testid="aje-new-credit" />
        <Input className="h-8 w-28" type="text" inputMode="decimal" placeholder="Jumlah" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="aje-new-amount" />
        <Select value={f.mis_type} onValueChange={(v) => setF({ ...f, mis_type: v })}><SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger><SelectContent>{["Factual", "Judgmental", "Projected"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        <Button size="sm" disabled={busy} onClick={add} data-testid="aje-add"><Plus size={14} className="mr-1" />Tambah</Button>
      </div>
      <EvidencePanel id={id} phase="Salah Saji / AJE-RJE" module="AJE" />
    </div>
  );
}

export function Notes({ id }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ issue: "", wp: "", severity: "High", assigned_to: "" });
  const scope = useRef({ id, generation: 0, request: 0 });
  if (scope.current.id !== id) scope.current = { id, generation: scope.current.generation + 1, request: 0 };
  const [busy, setBusy] = useState(false);
  const current = (generation) => scope.current.id === id && scope.current.generation === generation;
  const load = async () => {
    const generation = scope.current.generation, request = ++scope.current.request;
    try {
      const { data } = await api.get(`/engagements/${id}/review-notes`);
      if (current(generation) && scope.current.request === request) setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      if (current(generation) && scope.current.request === request) { setRows([]); toast.error(getErrorMessage(error)); }
    }
  };
  useEffect(() => {
    setRows([]); setF({ issue: "", wp: "", severity: "High", assigned_to: "" }); setBusy(false); load();
    return () => { scope.current.generation += 1; };
  }, [id]);
  const mutate = async (path, body, created = false) => {
    const generation = scope.current.generation;
    setBusy(true);
    try {
      await api.post(path, body);
      if (!current(generation)) return;
      if (created) { toast.success("Review note dibuka"); setF({ issue: "", wp: "", severity: "High", assigned_to: "" }); }
      await load();
    } catch (error) {
      if (current(generation)) {
        if ([403,404].includes(error.response?.status)) setRows([]);
        toast.error(getErrorMessage(error));
      }
    } finally { if (current(generation)) setBusy(false); }
  };
  const add = async () => {
    if (!f.issue.trim()) return toast.error("Isu wajib");
    await mutate(`/engagements/${id}/review-notes`, f, true);
  };
  const act = async (n, action) => {
    const text = window.prompt(action === "respond" ? "Tanggapan (wajib):" : "Alasan penutupan / pembukaan ulang (wajib):");
    if (text === null) return;
    if (!text.trim()) return toast.error("Dokumentasi wajib diisi");
    await mutate(`/engagements/${id}/review-notes/${n.id}/action`, { action, [action === "respond" ? "response" : "note"]: text.trim() });
  };
  return (
    <div className="space-y-3">
      <div className="bg-white border border-border rounded-sm overflow-x-auto"><table className="w-full audit-table">
        <thead><tr><th>Ref</th><th>Severity</th><th>Isu</th><th>Status</th><th>Riwayat</th><th></th></tr></thead>
        <tbody>{rows.map((n) => (
          <tr key={n.id} data-testid={`rn-row-${n.id}`}>
            <td className="font-mono text-xs">{n.ref}</td>
            <td><span className={`text-[10px] px-1.5 py-0.5 border rounded-sm ${n.severity === "Critical" || n.severity === "High" ? "bg-red-100 text-red-700 border-red-300" : "bg-muted"}`}>{n.severity}</span></td>
            <td>{n.issue}</td><td>{n.status}</td>
            <td className="text-[10px] text-muted-foreground">{(n.history || []).map((h) => `${h.action}`).join(" → ")}</td>
            <td className="flex gap-1">
              <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" disabled={busy || n.status === "Closed"} onClick={() => act(n, "respond")} data-testid={`rn-respond-${n.id}`}>Respon</Button>
              <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" disabled={busy || n.status !== "Responded"} onClick={() => act(n, "close")} data-testid={`rn-close-${n.id}`}>Tutup</Button>
              <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" disabled={busy || n.status !== "Closed"} onClick={() => act(n, "reopen")} data-testid={`rn-reopen-${n.id}`}>Buka Ulang</Button>
            </td>
          </tr>))}
          {rows.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-4">Belum ada review note.</td></tr>}
        </tbody></table></div>
      <div className="flex flex-wrap gap-2 items-end bg-white border border-border rounded-sm p-3">
        <Input className="h-8 w-64" placeholder="Isu" value={f.issue} onChange={(e) => setF({ ...f, issue: e.target.value })} data-testid="rn-new-issue" />
        <Input className="h-8 w-32" placeholder="WP Ref" value={f.wp} onChange={(e) => setF({ ...f, wp: e.target.value })} />
        <Select value={f.severity} onValueChange={(v) => setF({ ...f, severity: v })}><SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger><SelectContent>{["Critical", "High", "Medium", "Low"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Button size="sm" disabled={busy} onClick={add} data-testid="rn-add"><Plus size={14} className="mr-1" />Buka Note</Button>
      </div>
    </div>
  );
}

const checklistLabel = item => item.includes("_") ? item.toLowerCase().replace(/_/g," ").replace(/^./,letter=>letter.toUpperCase()) : item;

export function Complete({ id }) {
  const {data:d,error}=useReportingResource(id,"completion");
  if(error)return <div role="alert">Status penyelesaian tidak tersedia: {error}</div>;
  if(!d)return <div role="status">Memuat status penyelesaian…</div>;
  const blocked = d.blockers.length > 0;
  const passed = d.checklist.filter((c) => c.ok).length;
  return (
    <div className="space-y-4">
      <AlertBanner tone={blocked ? "danger" : "success"} title={`Status Penyelesaian: ${d.status}`} testid="cp-status">
        {blocked
          ? `${d.blockers.length} penghambat harus diselesaikan sebelum penyelesaian.`
          : "Seluruh prasyarat penyelesaian terpenuhi."}
      </AlertBanner>
      <div className="grid grid-cols-3 gap-3">
        <MetricCard label="Item Terpenuhi" value={`${passed}/${d.checklist.length}`} tone="success" testid="cp-metric-pass" />
        <MetricCard label="Penghambat" value={d.blockers.length} tone={blocked ? "danger" : "neutral"} testid="cp-metric-block" />
        <MetricCard label="Status" value={blocked ? "DIBLOKIR" : "SIAP"} tone={blocked ? "danger" : "success"} testid="cp-metric-status" />
      </div>
      {blocked && <details className="bg-white border border-border rounded-sm p-3" data-testid="cp-blocker-details"><summary className="cursor-pointer text-sm font-semibold">Tindakan yang diperlukan</summary><ul className="mt-2 space-y-2 text-sm text-red-700">{d.blockers.map((reason,index)=><li key={index}>{reason}</li>)}</ul></details>}
      <SectionCard title="Daftar Periksa Penyelesaian" testid="cp-checklist">
        <div className="space-y-0">
          {d.checklist.map((c, i) => (
            <div key={i} className="flex items-center gap-2.5 text-sm py-2 border-b border-border/50 last:border-0" data-testid={`cp-check-${i}`}>
              {c.ok
                ? <Check size={16} className="text-green-600 shrink-0" strokeWidth={2.4} />
                : <X size={16} className="text-red-600 shrink-0" strokeWidth={2.4} />}
              <span className={c.ok ? "text-foreground" : "text-red-700 font-medium"}>{checklistLabel(c.item)}</span>
            </div>
          ))}
        </div>
      </SectionCard>
      <EvidencePanel id={id} phase="Penyelesaian" module="Completion" />
    </div>
  );
}
